import { useState } from 'react';
import { ClipLoader } from 'react-spinners';
import toast from 'react-hot-toast';
import { PendingTruck } from './usePendingTrucks';
import {
  OwnershipStatus,
  TireBrandQuality,
  EngineCondition,
} from '../../../../../../../types/apiTypes';
import {
  formatNumberWithCommas,
  unformatNumber,
} from '../../../../../../../utils/CurrencyUtils';
import { FLEET_POLICY_LABEL } from '../fleet-insurance/fleetInsurance';

type DollarField = 'truck_payment_amount' | 'insurance_payment_amount';
type MileageField =
  | 'starting_mileage'
  | 'current_mileage'
  | 'typical_annual_mileage';

const formatDollarAmount = (value: number): string =>
  value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Allow digits, a single decimal point, and at most two decimal places.
const sanitizeDollar = (raw: string): string => {
  const cleaned = raw.replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length <= 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join('').slice(0, 2)}`;
};

const parseDollarDraft = (draft: string | undefined): number =>
  Math.max(0, parseFloat(draft ?? '') || 0);

const parseMileage = (raw: string): number =>
  Math.max(0, parseInt(unformatNumber(raw), 10) || 0);

export interface PendingTruckForm {
  ownership_status: OwnershipStatus;
  starting_mileage: number;
  current_mileage: number;
  typical_annual_mileage: number;
  truck_payment_amount: number;
  insurance_payment_amount: number;
  truck_class_number: number;
  pay_for_repairs: boolean;
  tire_brand_quality: TireBrandQuality;
  engine_condition: EngineCondition;
  truck_display_name: string;
  truck_make: string;
  truck_model: string;
  truck_year: number;
  vehicle_id_number: string;
}

interface PendingTruckCardProps {
  truck: PendingTruck;
  /**
   * THT-675 — customer is on one fleet-wide policy. The insurance field stays
   * visible but DISABLED and EMPTY with "Covered by fleet policy" (John,
   * 2026-08-20 — disabled, not hidden; §8/R3.2 — no number on screen). The
   * ELD-reported amount is still approved onto the truck: fleet mode stops the
   * customer EDITING the number, it does not throw it away (John, 2026-08-25).
   */
  isFleetInsurance?: boolean;
  onApprove: (
    truck_id: string,
    body: Partial<PendingTruckForm>
  ) => Promise<void>;
  onReject: (truck_id: string) => Promise<void>;
}

const OWNERSHIP_OPTIONS = Object.values(OwnershipStatus);
const TIRE_OPTIONS = Object.values(TireBrandQuality);
const ENGINE_OPTIONS = Object.values(EngineCondition);

const isOwnershipStatus = (value: string): value is OwnershipStatus =>
  (Object.values(OwnershipStatus) as string[]).includes(value);

const INPUT_BASE_CLASS =
  'mt-1 w-full rounded border border-[#EAECF0] px-2 py-1 text-[14px]';
const INPUT_CLASS = `${INPUT_BASE_CLASS} text-primary`;
/**
 * The locked (fleet-mode) variant SWAPS the text colour rather than appending
 * one — the two `text-*` utilities would otherwise both land on the element and
 * resolve by stylesheet order rather than intent. Previously this was built by
 * concatenating onto `INPUT_CLASS` with no separator, which fused
 * `text-primary` and `cursor-not-allowed` into one token that matched nothing,
 * so the locked input got neither (M1, John 2026-08-25).
 */
const LOCKED_INPUT_CLASS = `${INPUT_BASE_CLASS} cursor-not-allowed bg-[#F5F6F7] text-graysecondary`;
const LABEL_CLASS = 'text-[12px] text-graysecondary';

function PendingTruckCard({
  truck,
  isFleetInsurance = false,
  onApprove,
  onReject,
}: PendingTruckCardProps) {
  const [form, setForm] = useState<PendingTruckForm>({
    ownership_status: isOwnershipStatus(truck.ownership_status)
      ? truck.ownership_status
      : OwnershipStatus.Owned,
    starting_mileage: truck.starting_mileage || 0,
    current_mileage: truck.current_mileage || 0,
    typical_annual_mileage: truck.typical_annual_mileage || 0,
    truck_payment_amount: Number(truck.truck_payment_amount) || 0,
    insurance_payment_amount: Number(truck.insurance_payment_amount) || 0,
    truck_class_number: truck.truck_class_number || 8,
    pay_for_repairs: false,
    tire_brand_quality: TireBrandQuality.Premium,
    engine_condition: EngineCondition.Unmodified,
    truck_display_name: truck.truck_display_name || '',
    truck_make: truck.truck_make || '',
    truck_model: truck.truck_model || '',
    truck_year: truck.truck_year || new Date().getFullYear(),
    vehicle_id_number: truck.vehicle_id_number || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [confirmingReject, setConfirmingReject] = useState(false);
  // Dollar fields use a string draft while focused so intermediate states
  // like "12." survive re-render without being reformatted away.
  const [dollarDraft, setDollarDraft] = useState<
    Partial<Record<DollarField, string>>
  >({});

  const update = <K extends keyof PendingTruckForm>(
    key: K,
    value: PendingTruckForm[K]
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const getDollarDisplay = (key: DollarField): string => {
    const draft = dollarDraft[key];
    return draft !== undefined ? draft : formatDollarAmount(form[key]);
  };

  const onDollarFocus = (key: DollarField) => {
    const value = form[key];
    setDollarDraft((prev) => ({
      ...prev,
      [key]: value > 0 ? String(value) : '',
    }));
  };

  const onDollarChange = (key: DollarField, raw: string) => {
    setDollarDraft((prev) => ({ ...prev, [key]: sanitizeDollar(raw) }));
  };

  const onDollarBlur = (key: DollarField) => {
    update(key, parseDollarDraft(dollarDraft[key]));
    setDollarDraft((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Merge any pending dollar drafts into form — onBlur fires before onClick
  // but the setState is batched, so handleApprove must read drafts directly.
  const getFlushedForm = (): PendingTruckForm => {
    const flushed: PendingTruckForm = { ...form };
    (Object.keys(dollarDraft) as DollarField[]).forEach((key) => {
      flushed[key] = parseDollarDraft(dollarDraft[key]);
    });
    // THT-675 — no fleet-mode override here on purpose: the ELD-reported
    // premium is approved onto the truck as-is, so switching back to Each Truck
    // finds a real number on the record instead of a zero this screen put
    // there. Fleet mode blanks the input, never the payload.
    return flushed;
  };

  const handleApprove = async () => {
    const flushed = getFlushedForm();

    if (flushed.truck_year < 1940) {
      toast.error('Please enter a valid truck year (1940 or later).');
      return;
    }
    if (flushed.truck_class_number < 1 || flushed.truck_class_number > 12) {
      toast.error('Truck class number must be between 1 and 12.');
      return;
    }
    setSubmitting(true);
    try {
      const payload: Partial<PendingTruckForm> = { ...flushed };
      if (!payload.vehicle_id_number) delete payload.vehicle_id_number;
      if (!payload.truck_display_name) delete payload.truck_display_name;
      if (!payload.truck_make) delete payload.truck_make;
      if (!payload.truck_model) delete payload.truck_model;
      await onApprove(truck.truck_id, payload);
      toast.success('Truck approved');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to approve truck');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      await onReject(truck.truck_id);
      toast.success('Truck rejected');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to reject truck');
    } finally {
      setRejecting(false);
      setConfirmingReject(false);
    }
  };

  // NOTE: these are render helpers, not inner components — using them as
  // `<DollarInput .../>` would cause React to remount inputs on every render
  // and kill focus. Call them as `{renderDollarInput(...)}` instead.
  const renderDollarInput = (
    field: DollarField,
    label: string,
    lockedHelperText?: string
  ) => {
    // A locked field shows NO amount (R3.2 — "we don't need to show the old
    // number"): only the helper text saying why it cannot be edited. The
    // ELD-reported figure stays in `form` and is still what gets approved, so
    // blanking the display costs the record nothing.
    const locked = Boolean(lockedHelperText);
    return (
      <label className={LABEL_CLASS} key={field}>
        {label}
        <input
          type="text"
          inputMode="decimal"
          disabled={locked}
          className={locked ? LOCKED_INPUT_CLASS : INPUT_CLASS}
          value={locked ? '' : getDollarDisplay(field)}
          placeholder={locked ? '' : '0.00'}
          onFocus={() => onDollarFocus(field)}
          onChange={(e) => onDollarChange(field, e.target.value)}
          onBlur={() => onDollarBlur(field)}
        />
        {locked && (
          <span className="mt-1 block text-[11px] text-graysecondary">
            {lockedHelperText}
          </span>
        )}
      </label>
    );
  };

  const renderMileageInput = (field: MileageField, label: string) => (
    <label className={LABEL_CLASS} key={field}>
      {label}
      <input
        type="text"
        inputMode="numeric"
        className={INPUT_CLASS}
        value={formatNumberWithCommas(form[field])}
        placeholder="0"
        onChange={(e) => update(field, parseMileage(e.target.value))}
      />
    </label>
  );

  const renderEnumSelect = <F extends keyof PendingTruckForm>(
    field: F,
    label: string,
    options: readonly string[]
  ) => (
    <label className={LABEL_CLASS} key={field as string}>
      {label}
      <select
        className={INPUT_CLASS}
        value={form[field] as unknown as string}
        onChange={(e) => update(field, e.target.value as PendingTruckForm[F])}
      >
        {options.map((o) => (
          <option key={o} value={o} className="capitalize">
            {o}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="rounded-[8px] border border-[#EAECF0] bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between">
        <div>
          <h3 className="text-[16px] font-semibold text-primary">
            {truck.truck_display_name || 'Pending Truck'}
          </h3>
          <p className="text-[12px] text-graysecondary">
            From {truck.eld_type?.toUpperCase()} • VIN:{' '}
            {truck.vehicle_id_number || 'Unknown'}
          </p>
        </div>
        <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-[11px] font-medium text-yellow-800">
          Pending Approval
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className={LABEL_CLASS}>
          Display Name
          <input
            className={INPUT_CLASS}
            value={form.truck_display_name}
            onChange={(e) => update('truck_display_name', e.target.value)}
          />
        </label>
        <label className={LABEL_CLASS}>
          VIN
          <input
            className={INPUT_CLASS}
            value={form.vehicle_id_number}
            onChange={(e) =>
              update('vehicle_id_number', e.target.value.toUpperCase())
            }
          />
        </label>
        <label className={LABEL_CLASS}>
          Make
          <input
            className={INPUT_CLASS}
            value={form.truck_make}
            onChange={(e) => update('truck_make', e.target.value)}
          />
        </label>
        <label className={LABEL_CLASS}>
          Model
          <input
            className={INPUT_CLASS}
            value={form.truck_model}
            onChange={(e) => update('truck_model', e.target.value)}
          />
        </label>
        <label className={LABEL_CLASS}>
          Year
          <input
            type="number"
            min={1940}
            max={new Date().getFullYear() + 1}
            className={INPUT_CLASS}
            value={form.truck_year}
            onChange={(e) =>
              update(
                'truck_year',
                Math.max(0, parseInt(e.target.value, 10) || 0)
              )
            }
          />
        </label>
        {renderEnumSelect(
          'ownership_status',
          'Ownership Status',
          OWNERSHIP_OPTIONS
        )}
        {renderMileageInput('starting_mileage', 'Starting Mileage')}
        {renderMileageInput('current_mileage', 'Current Mileage')}
        {renderMileageInput('typical_annual_mileage', 'Typical Annual Mileage')}
        <label className={LABEL_CLASS}>
          Truck Class Number (1–12)
          <input
            type="number"
            min={1}
            max={12}
            className={INPUT_CLASS}
            value={form.truck_class_number}
            onChange={(e) =>
              update(
                'truck_class_number',
                Math.max(0, parseInt(e.target.value, 10) || 0)
              )
            }
          />
        </label>
        {renderDollarInput('truck_payment_amount', 'Truck Payment ($)')}
        {renderDollarInput(
          'insurance_payment_amount',
          'Insurance Payment ($)',
          isFleetInsurance ? FLEET_POLICY_LABEL : undefined
        )}
        {renderEnumSelect(
          'tire_brand_quality',
          'Tire Brand Quality',
          TIRE_OPTIONS
        )}
        {renderEnumSelect(
          'engine_condition',
          'Engine Condition',
          ENGINE_OPTIONS
        )}
        <label className={`${LABEL_CLASS} sm:col-span-2`}>
          <input
            type="checkbox"
            className="mr-2"
            checked={form.pay_for_repairs}
            onChange={(e) => update('pay_for_repairs', e.target.checked)}
          />
          I pay for repairs out of pocket
        </label>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        {confirmingReject ? (
          <>
            <span className="text-[12px] text-graysecondary">
              Reject this truck?
            </span>
            <button
              type="button"
              className="rounded border border-[#EAECF0] px-3 py-1 text-[13px]"
              onClick={() => setConfirmingReject(false)}
              disabled={rejecting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded bg-red-600 px-3 py-1 text-[13px] font-medium text-white disabled:opacity-60"
              onClick={handleReject}
              disabled={rejecting}
            >
              {rejecting ? (
                <ClipLoader size={12} color="#fff" />
              ) : (
                'Confirm Reject'
              )}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="rounded border border-red-300 px-3 py-1 text-[13px] text-red-600 disabled:opacity-60"
              onClick={() => setConfirmingReject(true)}
              disabled={submitting}
            >
              Reject
            </button>
            <button
              type="button"
              className="rounded bg-primary px-4 py-1 text-[13px] font-medium text-white disabled:opacity-60"
              onClick={handleApprove}
              disabled={submitting}
            >
              {submitting ? <ClipLoader size={12} color="#fff" /> : 'Approve'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default PendingTruckCard;
